// src/components/AboutUs/Company.jsx

import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BlueHeaderSection from "../BlueHeader";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.45, ease: "easeOut" },
  }),
};

const images = ["company3.jpg", "company1.jpg", "company2.jpg"];

const imageOverlays = [
  {
    title: "Innovation at the Core",
    desc: "Continuous R&D and modern architectures.",
  },
  {
    title: "Reliability in Delivery",
    desc: "Proven processes and quality-driven execution.",
  },
  {
    title: "Partnership Mindset",
    desc: "Long-term focus, not just project-based work.",
  },
];

const services = [
  {
    label: "Gen AI",
    path: "/services/gen-ai",
    title: "Generative AI Solutions",
    desc: "Custom LLMs, automation, and AI workflows tailored to your business.",
  },
  {
    label: "Mobile App Development",
    path: "/services/mobile-app-development",
    title: "Mobile App Development",
    desc: "User-centric, scalable mobile apps built for performance and engagement.",
  },
  {
    label: "Web Development",
    path: "/services/web-development",
    title: "Web Development",
    desc: "User-centric, scalable applications built for performance and engagement.",
  },
  {
    label: "DevOps Consulting",
    path: "/services/devops-consulting",
    title: "DevOps Consulting",
    desc: "Strategy, implementation, and optimization for streamlined, reliable operations.",
  },
  {
    label: "Salesforce Development",
    path: "/services/Salesforce-services",
    title: "Salesforce Development",
    desc: "Implementation, integration, and optimization to maximize your CRM ROI.",
  },
];

const Company = () => {
  const navigate = useNavigate();

  return (
    <>
      <BlueHeaderSection heading="OUR COMPANY" url="OUR COMPANY" />

      <section className="w-full bg-white text-black pt-4 md:pt-6 pb-10 px-6 md:px-20">
        <div className="max-w-7xl mx-auto text-center space-y-10">

          {/* MAIN HEADING */}
          <motion.h2
            custom={1}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold tracking-tight pt-10 text-[#1B2F5A]"
          >
            <span className="block text-center text-4xl sm:text-5xl font-bold text-[#1B2F5A]">
              Digital Evolution
            </span>
            <div className="w-24 h-1 bg-[#f7941d] mx-auto mt-4"></div>
          </motion.h2>

          {/* SUB HEADING */}
       <motion.h2
  custom={2}
  variants={fadeUp}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true }}
  /* Added leading-tight for better line spacing and tracking-tight for a premium feel */
  className="text-2xl md:text-3xl lg:text-4xl font-bold text-black mt-6 leading-tight tracking-tight"
>
  {/* Keeps "BytesFarms -" together */}
  <span className="inline-block whitespace-nowrap">BytesFarms —</span>{' '}
  {/* Keeps "Innovation and Transformation" together as a single phrase */}
  <span className="inline-block md:inline whitespace-nowrap">
    <span className="text-[#f7941d]">Innovation</span> and Transformation
  </span>
</motion.h2>

          {/* DESCRIPTION */}
          <motion.p
            custom={3}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-lg md:text-xl text-black mx-auto mt-[-10px]"
          >
            With over 20 years of industry experience, BytesFarms Technologies leads digital evolution with Salesforce development, cloud-native solutions, DevOps strategy, and web/mobile development — all combined into one powerful innovation engine.
          </motion.p>

          {/* IMAGE GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
            {images.map((img, i) => (
              <motion.div
                key={img}
                custom={i + 4}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="relative overflow-hidden rounded-2xl shadow-lg group transition-transform duration-700 hover:scale-105 hover:rotate-[1deg]"
              >
                <div className="relative w-full h-[250px] overflow-hidden">
                  <img
                    src={`${process.env.PUBLIC_URL}/Images/${img}`}
                    alt={`BytesFarms visual ${i + 1}`}
                    className="w-full h-full object-cover transform transition-all duration-700 ease-in-out group-hover:scale-110 group-hover:brightness-110 group-hover:-translate-y-1"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-10 group-hover:bg-opacity-30 transition-all duration-500" />
                  <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <h3 className="text-sm font-semibold tracking-wide">{imageOverlays[i].title}</h3>
                    <p className="text-xs">{imageOverlays[i].desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* VISION STATEMENT */}
          <motion.div
            custom={6}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-left md:text-center mt-20 space-y-6"
          >
            <h3 className="text-2xl md:text-3xl font-semibold">
              Your Trusted Digital Transformation Partner
            </h3>
            <p className="text-lg text-black">
              Our mission is to streamline operations, enhance user experiences, and drive measurable growth through future-proof solutions. As partners — not just vendors — we are committed to helping you scale with precision, agility, and insight.
            </p>
          </motion.div>

          {/* VISION & PHILOSOPHY */}
          <motion.div
            custom={7}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: {
                transition: { staggerChildren: 0.2 }
              }
            }}
            className="mt-12 grid md:grid-cols-2 gap-10 text-left"
          >
            {[
              {
                title: 'Vision & Evolution',
                text: 'From day one, BytesFarms was built to innovate. Our journey began with a single vision: harness technology to transform businesses. Today, we design and deliver tailored digital strategies engineered for sustainable growth across multiple domains.',
                slideFrom: -100
              },
              {
                title: 'Core Philosophy',
                text: 'Tailored solutions. Transparent partnerships. Scalable platforms. We focus on outcomes that truly move the needle — not fluff. Every decision is engineered for impact and long-term value.',
                slideFrom: 100
              }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, x: item.slideFrom },
                  visible: {
                    opacity: 1,
                    x: 0,
                    transition: { duration: 0.6, ease: 'easeOut' }
                  }
                }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                }}
                className="p-6 rounded-xl bg-white/80 backdrop-blur-md shadow-md transition-all duration-300 border-l-4 border-[#f7941d]"
              >
                <motion.h4
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ delay: 0.5 + i * 0.2, duration: 0.4 }}
                  className="text-xl font-bold text-[#f7941d] mb-2 relative"
                >
                  {item.title}
                  <span className="block w-12 bg-[#f7941d] mt-2 rounded-full"></span>
                </motion.h4>
                <p className="text-black">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* ── EXPERTISE CARDS — 5 cards with navigation ── */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mt-20 text-left space-y-6"
          >
            <motion.h3
              custom={7}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-[#1B2F5A] text-center"
            >
              Our Expertise
              <div className="w-16 h-1 bg-[#f7941d] mx-auto mt-3"></div>
            </motion.h3>

            <motion.div
              className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-6"
              variants={{
                visible: {
                  transition: { staggerChildren: 0.12 }
                }
              }}
            >
              {services.map((item, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.5, ease: "easeOut" }
                    }
                  }}
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 8px 24px rgba(247, 148, 29, 0.35)",
                  }}
                  onClick={() => navigate(item.path)}
                  className="cursor-pointer p-6 rounded-xl border border-[#f7941d] bg-white shadow-sm hover:bg-orange-50 transition-all duration-300 flex flex-col gap-3 group"
                >
                  {/* Orange accent top bar */}
                  <div className="w-8 h-1 bg-[#f7941d] rounded-full group-hover:w-full transition-all duration-500" />

                  <h5 className="font-semibold text-base text-[#1B2F5A] group-hover:text-[#f7941d] transition-colors duration-300">
                    {item.title}
                  </h5>
                  <p className="text-sm text-gray-600 flex-1">{item.desc}</p>

                  {/* Arrow CTA */}
                  <span className="text-[#f7941d] text-sm font-medium flex items-center gap-1 mt-auto group-hover:gap-2 transition-all duration-300">
                    Learn More
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* FOOTER CTA */}
          <motion.div
            custom={9}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mt-24"
          >
         <p className="text-base md:text-lg text-black mx-auto max-w-4xl px-4 leading-relaxed text-center">
  <span className="inline-block md:inline whitespace-nowrap">Ready to innovate?</span>{" "}
  <span className="text-[#f7941d] font-bold">BytesFarms</span> is your launchpad to 
  <span className="inline-block md:inline"> scalable, measurable, next-gen success.</span>
</p>
          </motion.div>

        </div>
      </section>
    </>
  );
};

export default Company;